/**
 * Determines the button variant based on the provided link type and foreground color.
 *
 * This hook memoizes the logic to resolve the appropriate `ButtonColorProps` variant
 * depending on design system theming which has become increasingly complex with implementation of M11Hero and M12Hero5050Module. The hook uses normalized color values to match
 * predefined variant maps for themes like "Black Friday" or "Christmas", and applies
 * a fallback when no match is found.
 *
 * @param {string} linkType - The type of the button link ('Primary', 'Secondary', 'Text').
 *                            Used to resolve which variant mapping logic to apply.
 * @param {string} foregroundColor - The base color value (hex or named) used for styling decisions.
 *                                   This is trimmed and lowercased before being compared to theme variants.
 *
 * @returns {ButtonColorProps} - The resolved button color variant used to style the button component.
 *
 * @example
 * const variant = useButtonVariant('Text', '#CFAD59');
 * // => 'new-link-black-friday'
 */

import { useMemo } from 'react';
import { baseVariantMap, linkTypeVariantMap } from './buttonVariantMap';
import { ButtonColorProps } from '~/lib';

export function useButtonVariant(linkType: string, foregroundColor: string): ButtonColorProps {
    return useMemo(() => {
        const normalized = foregroundColor.trim().toLowerCase();

        if (linkType === 'Secondary') {
            return linkTypeVariantMap.Secondary.default;
        }

        if (linkType === 'Text') {
            return linkTypeVariantMap.Text[normalized] || linkTypeVariantMap.Text.default;
        }

        return (
            baseVariantMap[normalized] ||
            linkTypeVariantMap.Default[normalized] ||
            linkTypeVariantMap.Default.default
        );
    }, [linkType, foregroundColor]);
}
